import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as BackIcon,
  MoreVert as MoreIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../i18n';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetIdentity } from 'react-admin';
import {
  learningGroupService,
  LearningGroup,
  LearningGroupMessage,
  LearningGroupMember,
} from '../../services/learningGroupService';
import { confirmLeaveGroup } from './confirmLeaveGroup';

const GroupDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [group, setGroup] = useState<LearningGroup | null>(null);
  const [messages, setMessages] = useState<LearningGroupMessage[]>([]);
  const [members, setMembers] = useState<LearningGroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<LearningGroupMember | null>(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState<any>(null);
  const [addingMember, setAddingMember] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({ name: '', description: '', isPublic: true, maxMembers: 0 });

  useEffect(() => {
    if (id) {
      loadGroup();
    }
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll for new messages (efficient - only fetch new ones)
  // Use ref for lastId to avoid stale closure issues
  const lastMessageIdRef = React.useRef(0);

  useEffect(() => {
    // Keep ref in sync with messages
    if (messages.length > 0) {
      lastMessageIdRef.current = Math.max(...messages.map((m) => m.id));
    }
  }, [messages]);

  useEffect(() => {
    if (!group || !id) return;

    let isActive = true;
    let timeoutId: ReturnType<typeof setTimeout>;
    const pollInterval = 3000;
    const groupId = parseInt(id);

    const pollNewMessages = async () => {
      if (!isActive) return;

      try {
        const newMessages = await learningGroupService.getMessagesSince(groupId, lastMessageIdRef.current);

        if (isActive && newMessages.length > 0) {
          setMessages((prev) => {
            // Deduplicate: only add messages not already in the list
            const existingIds = new Set(prev.map((m) => m.id));
            const unique = newMessages.filter((m: any) => !existingIds.has(m.id));
            return unique.length > 0 ? [...prev, ...unique] : prev;
          });
        }
      } catch (err) {
        console.error('Error polling messages:', err);
      }

      if (isActive) {
        timeoutId = setTimeout(pollNewMessages, pollInterval);
      }
    };

    timeoutId = setTimeout(pollNewMessages, pollInterval);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [group, id]);

  const loadGroup = async () => {
    setLoading(true);
    setError(null);
    try {
      const [groupData, messagesData, membersData] = await Promise.all([
        learningGroupService.getGroup(parseInt(id!)),
        learningGroupService.getMessages(parseInt(id!)),
        learningGroupService.getMembers(parseInt(id!)),
      ]);
      setGroup(groupData);
      setMessages(messagesData);
      setMembers(membersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading group');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const messagesData = await learningGroupService.getMessages(parseInt(id!));
      setMessages(messagesData);
    } catch (err) {
      console.error('Error loading messages:', err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !group || !identity) return;

    setSending(true);
    try {
      // #402: Absendername wird server-seitig aufgelöst — kein Client-Name mehr
      await learningGroupService.postMessage(group.id, newMessage.trim());
      setNewMessage('');
      loadMessages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error sending message');
    } finally {
      setSending(false);
    }
  };

  const handlePromoteMember = async (memberId: number) => {
    try {
      await learningGroupService.promoteMember(group!.id, memberId);
      loadGroup();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error promoting member');
    }
    setMenuAnchor(null);
  };

  const handleRemoveMember = async (member: LearningGroupMember) => {
    const name = member.userName || member.userEmail || 'Dieses Mitglied';
    if (!window.confirm(`${name} wirklich aus der Lerngruppe entfernen? Der Zugang zum Gruppenchat endet damit.`)) {
      setMenuAnchor(null);
      return;
    }
    try {
      await learningGroupService.removeMember(group!.id, member.id);
      loadGroup();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error removing member');
    }
    setMenuAnchor(null);
  };

  const handleOpenAddMember = async () => {
    setAddMemberOpen(true);
    setSelectedUserToAdd(null);
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const companyId = identity ? (identity as any).companyId : null;
      if (!companyId || !token) return;
      const response = await fetch(
        `${process.env.REACT_APP_SSO_URL}/api/users?company.id=${companyId}&itemsPerPage=500&order[lastName]=asc`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/ld+json' } }
      );
      if (response.ok) {
        const data = await response.json();
        const allUsers = data['hydra:member'] ?? data.member ?? [];
        // Filter out existing members
        const memberUserIds = new Set(members.map(m => m.userId));
        setAvailableUsers(allUsers.filter((u: any) => !memberUserIds.has(u.id)));
      }
    } catch {
      // non-critical
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserToAdd || !group) return;
    setAddingMember(true);
    try {
      await learningGroupService.addMember(group.id, selectedUserToAdd.id);
      setAddMemberOpen(false);
      setSelectedUserToAdd(null);
      loadGroup();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Hinzufügen');
    } finally {
      setAddingMember(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!group || !confirmLeaveGroup(group)) return;
    try {
      await learningGroupService.leaveGroup(group!.id);
      navigate('/learning-groups');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error leaving group');
    }
  };

  const handleOpenEditDialog = () => {
    if (!group) return;
    setEditData({
      name: group.name,
      description: group.description || '',
      isPublic: group.isPublic,
      maxMembers: group.maxMembers || 0,
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!group) return;
    try {
      await learningGroupService.updateGroup(group.id, editData);
      setEditDialogOpen(false);
      loadGroup();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    }
  };

  const handleDeleteGroup = async () => {
    if (!group) return;
    if (!window.confirm(`Lerngruppe "${group.name}" wirklich löschen? Alle Nachrichten und Mitgliedschaften werden entfernt.`)) return;
    try {
      await learningGroupService.deleteGroup(group.id);
      navigate('/learning-groups');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!group) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('learningGroups.notFound', 'Gruppe nicht gefunden')}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/learning-groups')} sx={{ mt: 2 }}>
          {t('common.back', 'Zurück')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)', gap: 2 }}>
      {/* Chat Area */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/learning-groups')}>
            <BackIcon />
          </IconButton>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <GroupIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{group.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('learningGroups.members', '%{count} Mitglieder', { count: group.memberCount })}
            </Typography>
          </Box>
          {group.canManage && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small" onClick={handleOpenEditDialog} title="Gruppe bearbeiten">
                <EditIcon />
              </IconButton>
              <IconButton size="small" color="error" onClick={handleDeleteGroup} title="Gruppe löschen">
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
          {/* #412: Austreten für jedes Mitglied, das nicht Gruppen-Admin ist —
              auch für Verwalter (Company-Admin), die einfache Mitglieder sind */}
          {group.isMember && !group.isAdmin && (
            <Button color="error" size="small" onClick={handleLeaveGroup}>
              {t('learningGroups.leave', 'Austreten')}
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ m: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {t('learningGroups.noMessages', 'Noch keine Nachrichten. Starten Sie die Unterhaltung!')}
              </Typography>
            </Box>
          ) : (
            messages.map((msg) => {
              const isOwnMessage = msg.userId === identity?.id;
              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '70%',
                      bgcolor: isOwnMessage ? 'primary.main' : 'grey.100',
                      color: isOwnMessage ? 'white' : 'text.primary',
                      borderRadius: 2,
                      p: 1.5,
                    }}
                  >
                    {!isOwnMessage && (
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {msg.userName}
                      </Typography>
                    )}
                    <Typography variant="body2">{msg.content}</Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        textAlign: 'right',
                        mt: 0.5,
                        opacity: 0.7,
                      }}
                    >
                      {new Date(msg.createdAt).toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input */}
        <Box
          component="form"
          onSubmit={handleSendMessage}
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            placeholder={t('learningGroups.messagePlaceholder', 'Nachricht schreiben...')}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            size="small"
            disabled={sending}
          />
          <IconButton
            type="submit"
            color="primary"
            disabled={!newMessage.trim() || sending}
            sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            {sending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Box>
      </Paper>

      {/* Members Sidebar */}
      <Paper sx={{ width: 280, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {t('learningGroups.membersList', 'Mitglieder')}
          </Typography>
          {group.canManage && (
            <IconButton size="small" color="primary" onClick={handleOpenAddMember} title="Mitglied hinzufügen">
              <PersonAddIcon />
            </IconButton>
          )}
        </Box>
        <List sx={{ flex: 1, overflow: 'auto' }}>
          {members.map((member) => (
            <ListItem
              key={member.id}
              secondaryAction={
                group.canManage && member.userId !== identity?.id && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setSelectedMember(member);
                      setMenuAnchor(e.currentTarget);
                    }}
                  >
                    <MoreIcon />
                  </IconButton>
                )
              }
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: member.role === 'admin' ? 'secondary.main' : 'grey.400' }}>
                  {member.role === 'admin' ? <AdminIcon /> : (member.userName || 'U').charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{member.userName || `User ${member.userId}`}</span>
                    {member.role === 'admin' && (
                      <Chip label="Admin" size="small" color="secondary" />
                    )}
                  </Box>
                }
                secondary={member.userEmail || new Date(member.joinedAt).toLocaleDateString('de-DE')}
              />
            </ListItem>
          ))}
        </List>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          {selectedMember && selectedMember.role !== 'admin' && (
            <MenuItem onClick={() => handlePromoteMember(selectedMember.id)}>
              {t('learningGroups.promote', 'Zum Admin machen')}
            </MenuItem>
          )}
          <MenuItem onClick={() => selectedMember && handleRemoveMember(selectedMember)}>
            {t('learningGroups.removeMember', 'Entfernen')}
          </MenuItem>
        </Menu>
      </Paper>

      {/* Add Member Dialog */}
      <Dialog open={addMemberOpen} onClose={() => setAddMemberOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mitglied hinzufügen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Wählen Sie einen Benutzer aus, der zur Gruppe "{group?.name}" hinzugefügt werden soll.
            Der Benutzer erhält eine Benachrichtigung.
          </Typography>
          <Autocomplete
            options={availableUsers}
            getOptionLabel={(option: any) =>
              `${option.firstName || ''} ${option.lastName || ''} (${option.email})`.trim()
            }
            value={selectedUserToAdd}
            onChange={(_, newValue) => setSelectedUserToAdd(newValue)}
            loading={loadingUsers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Benutzer suchen"
                placeholder="Name oder E-Mail..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers && <CircularProgress size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleAddMember}
            disabled={!selectedUserToAdd || addingMember}
            startIcon={addingMember ? <CircularProgress size={16} /> : <PersonAddIcon />}
          >
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Lerngruppe bearbeiten</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            value={editData.name}
            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            fullWidth
          />
          <TextField
            label="Beschreibung"
            value={editData.description}
            onChange={(e) => setEditData({ ...editData, description: e.target.value })}
            fullWidth
            multiline
            rows={3}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Max. Mitglieder (0 = unbegrenzt)"
              type="number"
              value={editData.maxMembers}
              onChange={(e) => setEditData({ ...editData, maxMembers: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
            <Button
              variant={editData.isPublic ? 'contained' : 'outlined'}
              onClick={() => setEditData({ ...editData, isPublic: !editData.isPublic })}
              sx={{ minWidth: 120 }}
            >
              {editData.isPublic ? 'Öffentlich' : 'Privat'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={!editData.name.trim()}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupDetail;
